import { isSquishyCostume, type ClothingItem } from '../schemas/data/clothing.js';

/**
 * The clothing catalog (design doc §23; issue #43): Keeper clothing and
 * squishy accessories in one table. Ids are stored in players' rows: add
 * freely, never rename or remove one. Shapes and colours are first guesses to
 * judge in the Keeper gallery (`/keepers.html?items=…`).
 *
 * - **Starter** items (`sources: ['starter']`) are every account's from the
 *   start, so the wardrobe is never empty (DECISIONS "Wardrobe (#43)").
 * - **Everyday** items are found all year; **Halloween** items only drop in
 *   the Halloween window (design doc §15) and stay wearable forever.
 * - Where items drop, and how often, is server-only
 *   (`data/server/clothing-drops.ts`, CLAUDE.md rule 6).
 * - **Boutique** items (`boutique` source, #45) can also be bought with
 *   Patch Coins, priced by rarity within each slot (`BOUTIQUE_RULES` checks
 *   the range). Whole costumes cost 3 to 8 busy days of coins (#261).
 *   Legendary pieces stay found-only, except the Marigold Calavera (owner,
 *   #261); Mythic pieces and future milestone rewards (Cozy Apron, Squishy
 *   Net, design doc §24) are never sold.
 *
 * Pieces sit in their socket's units (`ClothingPieceSchema`): +y up, −z the
 * front, x across. Turns are degrees.
 */

// ── Starter set: one per slot but costume ────────────────────────────────
const STARTER: ClothingItem[] = [
  {
    id: 'sunny-cap',
    name: 'Sunny Cap',
    description: 'A comfy cap for sunny days and squinty smiles.',
    slot: 'hat',
    rarity: 'common',
    sources: ['starter'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.02, 0.04], size: [1.0, 0.62, 1.02], color: '#ffd166' },
        { shape: 'ellipsoid', at: [0, -0.16, -0.5], size: [0.8, 0.08, 0.55], color: '#ffb347' },
        { shape: 'ellipsoid', at: [0, 0.3, 0.04], size: [0.14, 0.1, 0.14], color: '#ffb347' },
      ],
    },
  },
  {
    id: 'daisy-clip',
    name: 'Daisy Clip',
    description: 'A little daisy that never needs watering.',
    slot: 'hair-accessory',
    rarity: 'common',
    sources: ['starter'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0, 0], size: [1.0, 1.0, 0.3], color: '#ffffff' },
        { shape: 'ellipsoid', at: [0, 0, -0.1], size: [0.42, 0.42, 0.25], color: '#ffd166' },
      ],
    },
  },
  {
    id: 'cozy-sweater',
    name: 'Cozy Sweater',
    description: 'Knitted with extra snuggles.',
    slot: 'top',
    rarity: 'common',
    sources: ['starter'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.02, 0], size: [1.1, 1.08, 1.1], color: '#ff9fb2' },
        { shape: 'ellipsoid', at: [0, 0.46, 0], size: [0.72, 0.2, 0.75], color: '#fff1e6' },
        { shape: 'ellipsoid', at: [0, 0.08, -0.53], size: [0.3, 0.26, 0.08], color: '#fff1e6' },
      ],
    },
  },
  {
    id: 'picnic-skirt',
    name: 'Picnic Skirt',
    description: 'Checked, swishy and ready for sandwiches.',
    slot: 'bottom',
    rarity: 'common',
    sources: ['starter'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'cone', at: [0, -0.15, 0], size: [1.4, 1.5, 1.4], color: '#7fc8a9' },
        { shape: 'ellipsoid', at: [0, 0.32, 0], size: [1.06, 0.22, 1.06], color: '#ffffff' },
      ],
    },
  },
  {
    id: 'puddle-boots',
    name: 'Puddle Boots',
    description: 'Splash first, ask questions later.',
    slot: 'shoes',
    rarity: 'common',
    sources: ['starter'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.1, 0], size: [1.15, 1.2, 1.1], color: '#ffd84d' },
        { shape: 'capsule', at: [0, 0.75, 0.15], size: [0.8, 0.9, 0.7], color: '#ffd84d' },
      ],
    },
  },
  {
    id: 'acorn-backpack',
    name: 'Acorn Backpack',
    description: 'Room for snacks, treasures and one sleepy squishy.',
    slot: 'back',
    rarity: 'common',
    sources: ['starter'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.08, 0.6], size: [0.78, 0.82, 0.48], color: '#c98a4b' },
        { shape: 'ellipsoid', at: [0, 0.3, 0.6], size: [0.84, 0.36, 0.52], color: '#7a4f2c' },
        { shape: 'capsule', at: [0, 0.52, 0.6], size: [0.1, 0.2, 0.1], color: '#7a4f2c' },
      ],
    },
  },
  {
    id: 'firefly-lantern',
    name: 'Firefly Lantern',
    description: 'A friendly glow for walks after dusk.',
    slot: 'held',
    rarity: 'common',
    sources: ['starter'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'capsule', at: [0, 0.35, 0], size: [0.12, 0.5, 0.12], color: '#6b4430' },
        { shape: 'ellipsoid', at: [0, -0.05, 0], size: [0.5, 0.6, 0.5], color: '#fff3a8' },
        { shape: 'cone', at: [0, 0.3, 0], size: [0.45, 0.25, 0.45], color: '#6b4430' },
      ],
    },
  },
];

// ── Everyday: found all year ─────────────────────────────────────────────
const EVERYDAY: ClothingItem[] = [
  {
    id: 'pom-pom-beanie',
    name: 'Pom-Pom Beanie',
    description: 'Warm ears and a bouncy pom-pom on top.',
    slot: 'hat',
    rarity: 'common',
    sources: ['found', 'boutique'],
    boutiquePrice: 15, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.04, 0.04], size: [1.02, 0.7, 1.04], color: '#7fa8f0' },
        { shape: 'ellipsoid', at: [0, -0.28, 0.04], size: [1.06, 0.18, 1.08], color: '#fff4c2' },
        { shape: 'ellipsoid', at: [0, 0.36, 0.04], size: [0.32, 0.3, 0.32], color: '#fff4c2' },
      ],
    },
  },
  {
    id: 'flower-crown',
    name: 'Flower Crown',
    description: 'Picked from the sunniest corner of the meadow.',
    slot: 'hat',
    rarity: 'uncommon',
    sources: ['found', 'boutique'],
    boutiquePrice: 30, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.1, 0], size: [1.02, 0.12, 1.02], color: '#7fc8a9' },
        { shape: 'ellipsoid', at: [0, -0.04, -0.48], size: [0.2, 0.2, 0.14], color: '#ff8fab' },
        {
          shape: 'ellipsoid',
          at: [-0.38, -0.04, -0.3],
          size: [0.18, 0.18, 0.14],
          color: '#ffd166',
        },
        { shape: 'ellipsoid', at: [0.38, -0.04, -0.3], size: [0.18, 0.18, 0.14], color: '#c4a8f0' },
        { shape: 'ellipsoid', at: [-0.48, -0.04, 0.1], size: [0.17, 0.17, 0.14], color: '#ff8fab' },
        { shape: 'ellipsoid', at: [0.48, -0.04, 0.1], size: [0.17, 0.17, 0.14], color: '#ffd166' },
      ],
    },
  },
  {
    id: 'starry-crown',
    name: 'Starry Crown',
    description: 'Fit for the ruler of a very cozy kingdom.',
    slot: 'hat',
    rarity: 'rare',
    sources: ['found', 'boutique'],
    boutiquePrice: 60, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'capsule', at: [0, 0.02, 0], size: [0.82, 0.24, 0.82], color: '#ffd84d' },
        { shape: 'cone', at: [0, 0.24, -0.38], size: [0.2, 0.32, 0.2], color: '#ffd84d' },
        { shape: 'cone', at: [-0.3, 0.2, -0.24], size: [0.18, 0.26, 0.18], color: '#ffd84d' },
        { shape: 'cone', at: [0.3, 0.2, -0.24], size: [0.18, 0.26, 0.18], color: '#ffd84d' },
        { shape: 'ellipsoid', at: [0, 0.04, -0.42], size: [0.14, 0.14, 0.08], color: '#ff6f91' },
      ],
    },
  },
  {
    id: 'heart-clip',
    name: 'Heart Clip',
    description: 'Wear your heart in your hair.',
    slot: 'hair-accessory',
    rarity: 'common',
    sources: ['found', 'boutique'],
    boutiquePrice: 15, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [-0.18, 0.1, 0], size: [0.55, 0.55, 0.3], color: '#ff6f91' },
        { shape: 'ellipsoid', at: [0.18, 0.1, 0], size: [0.55, 0.55, 0.3], color: '#ff6f91' },
        {
          shape: 'cone',
          at: [0, -0.2, 0],
          size: [0.6, 0.5, 0.3],
          turn: [0, 0, 180],
          color: '#ff6f91',
        },
      ],
    },
  },
  {
    id: 'big-bow',
    name: 'Big Bow',
    description: 'The bigger the bow, the bigger the smile.',
    slot: 'hair-accessory',
    rarity: 'uncommon',
    sources: ['found', 'boutique'],
    boutiquePrice: 30, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'teardrop',
          at: [-0.4, 0, 0],
          size: [0.7, 0.8, 0.35],
          turn: [0, 0, 90],
          color: '#ff6f91',
        },
        {
          shape: 'teardrop',
          at: [0.4, 0, 0],
          size: [0.7, 0.8, 0.35],
          turn: [0, 0, -90],
          color: '#ff6f91',
        },
        { shape: 'ellipsoid', at: [0, 0, 0], size: [0.34, 0.36, 0.36], color: '#e24f78' },
      ],
    },
  },
  {
    id: 'cozy-apron',
    name: 'Cozy Apron',
    description: 'For baking, gardening and wiping sticky squishies.',
    slot: 'top',
    rarity: 'common',
    sources: ['found'],
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.12, -0.42], size: [0.78, 0.95, 0.22], color: '#fff1e6' },
        { shape: 'ellipsoid', at: [0, -0.3, -0.52], size: [0.42, 0.22, 0.08], color: '#ff8fab' },
        { shape: 'ellipsoid', at: [0, 0.05, 0], size: [1.08, 0.12, 1.08], color: '#fff1e6' },
      ],
    },
  },
  {
    id: 'rainbow-scarf',
    name: 'Rainbow Scarf',
    description: 'Every color, all at once, all wrapped up.',
    slot: 'top',
    rarity: 'uncommon',
    sources: ['found', 'boutique'],
    boutiquePrice: 30, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.44, 0], size: [1.12, 0.24, 1.12], color: '#ff8fab' },
        { shape: 'ellipsoid', at: [0, 0.34, 0], size: [1.1, 0.16, 1.1], color: '#ffd166' },
        { shape: 'capsule', at: [0.24, 0.1, -0.5], size: [0.2, 0.48, 0.1], color: '#7fa8f0' },
        { shape: 'capsule', at: [0.36, 0.06, -0.48], size: [0.18, 0.42, 0.1], color: '#9fd88b' },
      ],
    },
  },
  {
    // The tutorial's reward (#24, design doc §26 step 12): account-bound.
    id: 'seedling-scarf',
    name: 'Seedling Scarf',
    description: 'Knitted from the first leaves of your Heart Seed.',
    slot: 'top',
    rarity: 'uncommon',
    sources: ['tutorial'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.44, 0], size: [1.12, 0.24, 1.12], color: '#7fc97f' },
        { shape: 'ellipsoid', at: [0, 0.34, 0], size: [1.1, 0.16, 1.1], color: '#a8e6a1' },
        { shape: 'capsule', at: [0.24, 0.1, -0.5], size: [0.2, 0.48, 0.1], color: '#7fc97f' },
        { shape: 'ellipsoid', at: [0.3, -0.16, -0.52], size: [0.26, 0.12, 0.2], color: '#5fb85f' },
      ],
    },
  },
  {
    id: 'twirly-tutu',
    name: 'Twirly Tutu',
    description: 'Spins all by itself. Probably.',
    slot: 'bottom',
    rarity: 'uncommon',
    sources: ['found', 'boutique'],
    boutiquePrice: 30, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0, 0], size: [1.5, 0.5, 1.5], color: '#ffc4e1' },
        { shape: 'ellipsoid', at: [0, -0.12, 0], size: [1.62, 0.32, 1.62], color: '#ffe3f0' },
      ],
    },
  },
  {
    id: 'bunny-slippers',
    name: 'Bunny Slippers',
    description: 'Hop-hop-hop wherever you go.',
    slot: 'shoes',
    rarity: 'rare',
    sources: ['found', 'boutique'],
    boutiquePrice: 60, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.08, 0], size: [1.2, 1.15, 1.12], color: '#fff6fb' },
        {
          shape: 'capsule',
          at: [-0.22, 0.9, -0.2],
          size: [0.26, 0.9, 0.2],
          turn: [-15, 0, 10],
          color: '#fff6fb',
        },
        {
          shape: 'capsule',
          at: [0.22, 0.9, -0.2],
          size: [0.26, 0.9, 0.2],
          turn: [-15, 0, -10],
          color: '#fff6fb',
        },
        { shape: 'ellipsoid', at: [0, 0.3, -0.52], size: [0.2, 0.16, 0.12], color: '#ff9db5' },
      ],
    },
  },
  {
    id: 'cloud-cape',
    name: 'Cloud Cape',
    description: 'Soft as a cloud, and only a little bit damp.',
    slot: 'back',
    rarity: 'rare',
    sources: ['found', 'boutique'],
    boutiquePrice: 60, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.35, 0.5], size: [1.2, 1.6, 0.2], color: '#e8f1ff' },
        {
          shape: 'ellipsoid',
          at: [-0.32, -1.05, 0.52],
          size: [0.55, 0.42, 0.24],
          color: '#e8f1ff',
        },
        { shape: 'ellipsoid', at: [0.32, -1.05, 0.52], size: [0.55, 0.42, 0.24], color: '#e8f1ff' },
        { shape: 'ellipsoid', at: [0, 0.42, 0], size: [1.1, 0.18, 1.1], color: '#ffffff' },
      ],
    },
  },
  {
    id: 'butterfly-wings',
    name: 'Butterfly Wings',
    description: 'Flutter, flutter. No flying, but lots of fluttering.',
    slot: 'back',
    rarity: 'epic',
    sources: ['found', 'boutique'],
    boutiquePrice: 100, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'ellipsoid',
          at: [-0.62, 0.32, 0.6],
          size: [1.0, 1.1, 0.08],
          turn: [0, -20, 25],
          color: '#c4a8f0',
        },
        {
          shape: 'ellipsoid',
          at: [0.62, 0.32, 0.6],
          size: [1.0, 1.1, 0.08],
          turn: [0, 20, -25],
          color: '#c4a8f0',
        },
        {
          shape: 'ellipsoid',
          at: [-0.5, -0.35, 0.6],
          size: [0.65, 0.7, 0.08],
          turn: [0, -20, -20],
          color: '#ff9fd6',
        },
        {
          shape: 'ellipsoid',
          at: [0.5, -0.35, 0.6],
          size: [0.65, 0.7, 0.08],
          turn: [0, 20, 20],
          color: '#ff9fd6',
        },
      ],
    },
  },
  {
    id: 'bubble-wand',
    name: 'Bubble Wand',
    description: 'One wave, a hundred tiny bubbles.',
    slot: 'held',
    rarity: 'uncommon',
    sources: ['found', 'boutique'],
    boutiquePrice: 30, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'capsule', at: [0, 0.4, 0], size: [0.1, 0.9, 0.1], color: '#7fd1b9' },
        {
          shape: 'arc',
          at: [0, 0.95, 0],
          size: [0.5, 0.5, 0.1],
          turn: [90, 0, 0],
          color: '#ff8fab',
        },
        { shape: 'ellipsoid', at: [0.35, 1.25, -0.1], size: [0.3, 0.3, 0.3], color: '#d6f0ff' },
      ],
    },
  },
  {
    id: 'squishy-net',
    name: 'Squishy Net',
    description: 'For catching butterflies. Squishies prefer hugs.',
    slot: 'held',
    rarity: 'common',
    sources: ['found'],
    tradable: true,
    visual: {
      pieces: [
        { shape: 'capsule', at: [0, 0.5, 0], size: [0.1, 1.2, 0.1], color: '#c98a4b' },
        { shape: 'ellipsoid', at: [0, 1.25, 0], size: [0.6, 0.12, 0.6], color: '#ffffff' },
        {
          shape: 'cone',
          at: [0, 1.05, 0],
          size: [0.55, 0.45, 0.55],
          turn: [180, 0, 0],
          color: '#e8f1ff',
        },
      ],
    },
  },
  {
    id: 'cloud-onesie',
    name: 'Cloud Onesie',
    description: 'Be a fluffy cloud. Drift around. Have a nap.',
    slot: 'costume',
    rarity: 'legendary',
    sources: ['found'],
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.2, 0], size: [0.95, 0.66, 1.0], color: '#f3f7ff' },
        { shape: 'ellipsoid', at: [0, 0.22, 0.12], size: [1.0, 0.6, 0.9], color: '#f3f7ff' },
        { shape: 'ellipsoid', at: [-0.38, 0.46, 0.12], size: [0.42, 0.3, 0.42], color: '#ffffff' },
        { shape: 'ellipsoid', at: [0.38, 0.46, 0.12], size: [0.42, 0.3, 0.42], color: '#ffffff' },
        { shape: 'ellipsoid', at: [0, 0.52, 0.1], size: [0.5, 0.3, 0.5], color: '#ffffff' },
      ],
    },
  },
];

// ── Halloween: found only in the Halloween window ────────────────────────
const HALLOWEEN: ClothingItem[] = [
  {
    id: 'witch-hat',
    name: 'Witch Hat',
    description: 'Pointy, purple and a tiny bit magical.',
    slot: 'hat',
    rarity: 'uncommon',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 30, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.1, 0.04], size: [1.5, 0.1, 1.5], color: '#6b4a7a' },
        { shape: 'cone', at: [0, 0.38, 0.04], size: [0.8, 1.0, 0.8], color: '#6b4a7a' },
        { shape: 'capsule', at: [0, 0.02, 0.04], size: [0.82, 0.14, 0.82], color: '#ffa552' },
      ],
    },
  },
  {
    id: 'pumpkin-hood',
    name: 'Pumpkin Hood',
    description: 'Snug as a pumpkin, with a stem on top.',
    slot: 'hat',
    rarity: 'rare',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 60, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.1, 0.1], size: [1.12, 0.85, 1.1], color: '#ff9a3c' },
        { shape: 'ellipsoid', at: [-0.3, -0.1, 0.1], size: [0.5, 0.82, 1.0], color: '#f08a2c' },
        { shape: 'ellipsoid', at: [0.3, -0.1, 0.1], size: [0.5, 0.82, 1.0], color: '#f08a2c' },
        {
          shape: 'capsule',
          at: [0, 0.38, 0.1],
          size: [0.12, 0.26, 0.12],
          turn: [0, 0, 12],
          color: '#6a9a5a',
        },
      ],
    },
  },
  {
    id: 'bat-clip',
    name: 'Bat Clip',
    description: 'A tiny bat that hangs out in your hair.',
    slot: 'hair-accessory',
    rarity: 'common',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 15, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0, 0], size: [0.4, 0.42, 0.34], color: '#4a3557' },
        {
          shape: 'teardrop',
          at: [-0.4, 0.05, 0],
          size: [0.45, 0.65, 0.12],
          turn: [0, 0, 70],
          color: '#4a3557',
        },
        {
          shape: 'teardrop',
          at: [0.4, 0.05, 0],
          size: [0.45, 0.65, 0.12],
          turn: [0, 0, -70],
          color: '#4a3557',
        },
      ],
    },
  },
  {
    id: 'candy-corn-clip',
    name: 'Candy Corn Clip',
    description: 'Looks yummy. Please do not eat it.',
    slot: 'hair-accessory',
    rarity: 'common',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 15, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'cone', at: [0, 0.05, 0], size: [0.6, 0.85, 0.3], color: '#ffffff' },
        { shape: 'cone', at: [0, -0.1, 0], size: [0.66, 0.55, 0.32], color: '#ffa552' },
        { shape: 'ellipsoid', at: [0, -0.32, 0], size: [0.62, 0.24, 0.32], color: '#ffd84d' },
      ],
    },
  },
  {
    id: 'moonlit-sweater',
    name: 'Moonlit Sweater',
    description: 'Midnight blue, with a smiling moon.',
    slot: 'top',
    rarity: 'uncommon',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 30, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.02, 0], size: [1.1, 1.08, 1.1], color: '#3f4f8c' },
        { shape: 'ellipsoid', at: [0, 0.08, -0.54], size: [0.36, 0.36, 0.08], color: '#fff4c2' },
        { shape: 'ellipsoid', at: [0.07, 0.1, -0.58], size: [0.26, 0.3, 0.06], color: '#3f4f8c' },
      ],
    },
  },
  {
    id: 'pumpkin-bloomers',
    name: 'Pumpkin Bloomers',
    description: 'Round, orange and extremely puffy.',
    slot: 'bottom',
    rarity: 'uncommon',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 30, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.05, 0], size: [1.35, 0.95, 1.3], color: '#ff9a3c' },
        { shape: 'ellipsoid', at: [0, 0.32, 0], size: [1.08, 0.2, 1.08], color: '#6a9a5a' },
      ],
    },
  },
  {
    id: 'curly-witch-boots',
    name: 'Curly Witch Boots',
    description: 'The toes curl up when you giggle.',
    slot: 'shoes',
    rarity: 'rare',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 60, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.08, 0.05], size: [1.12, 1.15, 1.0], color: '#4a3557' },
        {
          shape: 'cone',
          at: [0, 0.3, -0.62],
          size: [0.5, 0.7, 0.5],
          turn: [-70, 0, 0],
          color: '#4a3557',
        },
        { shape: 'capsule', at: [0, 0.75, 0.1], size: [0.85, 0.9, 0.75], color: '#6b4a7a' },
        { shape: 'ellipsoid', at: [0, 0.32, -0.5], size: [0.2, 0.2, 0.12], color: '#ffd84d' },
      ],
    },
  },
  {
    id: 'ghost-cape',
    name: 'Ghost Cape',
    description: 'Swooshy and see-through. Well, almost.',
    slot: 'back',
    rarity: 'rare',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 60, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'teardrop',
          at: [0, -0.4, 0.5],
          size: [1.3, 1.8, 0.2],
          turn: [0, 0, 180],
          color: '#f8f4ff',
        },
        { shape: 'ellipsoid', at: [0, 0.42, 0], size: [1.1, 0.2, 1.1], color: '#f8f4ff' },
        { shape: 'ellipsoid', at: [0, 0.42, -0.55], size: [0.18, 0.18, 0.12], color: '#b39ddb' },
      ],
    },
  },
  {
    id: 'bat-wings',
    name: 'Bat Wings',
    description: 'Flap flap! Upside-down naps not included.',
    slot: 'back',
    rarity: 'epic',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 100, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'teardrop',
          at: [-0.7, 0.2, 0.6],
          size: [0.8, 1.3, 0.08],
          turn: [0, -15, 60],
          color: '#4a3557',
        },
        {
          shape: 'teardrop',
          at: [0.7, 0.2, 0.6],
          size: [0.8, 1.3, 0.08],
          turn: [0, 15, -60],
          color: '#4a3557',
        },
        { shape: 'ellipsoid', at: [0, 0.1, 0.55], size: [0.3, 0.4, 0.2], color: '#4a3557' },
      ],
    },
  },
  {
    id: 'jack-o-lamp',
    name: "Jack-o'-Lamp",
    description: 'A pumpkin with a cheeky grin and a cozy glow.',
    slot: 'held',
    rarity: 'uncommon',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 30, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'capsule', at: [0, 0.45, 0], size: [0.1, 0.4, 0.1], color: '#6b4430' },
        { shape: 'ellipsoid', at: [0, -0.02, 0], size: [0.62, 0.52, 0.6], color: '#ff9a3c' },
        { shape: 'ellipsoid', at: [-0.1, 0.04, -0.29], size: [0.1, 0.1, 0.05], color: '#ffe48a' },
        { shape: 'ellipsoid', at: [0.1, 0.04, -0.29], size: [0.1, 0.1, 0.05], color: '#ffe48a' },
        {
          shape: 'arc',
          at: [0, -0.1, -0.3],
          size: [0.26, 0.1, 0.05],
          turn: [0, 180, 0],
          color: '#ffe48a',
        },
      ],
    },
  },
  {
    id: 'tiny-broom',
    name: 'Tiny Broom',
    description: 'Too small to ride. Perfect for sweeping up crumbs.',
    slot: 'held',
    rarity: 'rare',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 60, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      pieces: [
        { shape: 'capsule', at: [0, 0.4, 0], size: [0.1, 1.3, 0.1], color: '#a0703f' },
        { shape: 'cone', at: [0, -0.35, 0], size: [0.45, 0.5, 0.45], color: '#e8c068' },
        { shape: 'capsule', at: [0, -0.12, 0], size: [0.24, 0.08, 0.24], color: '#6b4a7a' },
      ],
    },
  },
  {
    id: 'ghost-sheet',
    name: 'Ghost Sheet',
    description: 'A friendly ghost who mostly says "boo" and giggles.',
    slot: 'costume',
    rarity: 'rare',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 210, // TUNE: a whole costume, priced with the #261 costume rack
    tradable: true,
    visual: {
      pieces: [
        { shape: 'teardrop', at: [0, 0.02, 0], size: [1.2, 1.12, 1.2], color: '#f8f4ff' },
        // A dome over the head, so the sheet hides the face.
        { shape: 'ellipsoid', at: [0, 0.2, 0], size: [1.12, 0.66, 1.34], color: '#f8f4ff' },
        { shape: 'ellipsoid', at: [-0.14, 0.24, -0.66], size: [0.1, 0.13, 0.05], color: '#3b2a3f' },
        { shape: 'ellipsoid', at: [0.14, 0.24, -0.66], size: [0.1, 0.13, 0.05], color: '#3b2a3f' },
        { shape: 'ellipsoid', at: [0, 0.12, -0.66], size: [0.08, 0.08, 0.04], color: '#ff9db5' },
      ],
    },
  },
  {
    id: 'squishy-onesie',
    name: 'Squishy Onesie',
    description: 'Dress up as your favorite squishy, ears and all.',
    slot: 'costume',
    rarity: 'legendary',
    season: 'halloween',
    sources: ['found'],
    tradable: true,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.2, 0], size: [0.95, 0.66, 1.0], color: '#a8e6cf' },
        { shape: 'ellipsoid', at: [0, 0.22, 0.12], size: [1.0, 0.6, 0.9], color: '#a8e6cf' },
        {
          shape: 'teardrop',
          at: [-0.3, 0.52, 0.1],
          size: [0.24, 0.3, 0.16],
          turn: [0, 0, 20],
          color: '#a8e6cf',
        },
        {
          shape: 'teardrop',
          at: [0.3, 0.52, 0.1],
          size: [0.24, 0.3, 0.16],
          turn: [0, 0, -20],
          color: '#a8e6cf',
        },
        { shape: 'ellipsoid', at: [0, -0.18, -0.5], size: [0.5, 0.4, 0.06], color: '#fff6e0' },
      ],
    },
  },
];

// ── Squishy accessories (one per squishy) ────────────────────────────────
// Pieces are in the squishy's body sizes (the client's `accessory.ts`, #340):
// from its crown (on top of the head, in head sizes) or its neck (a ring
// under the face, in the body's width there). Crown pieces are 2.5× their
// first guesses (owner, #340), so a hat covers about half the head.
const SQUISHY: ClothingItem[] = [
  {
    id: 'tiny-bow',
    name: 'Tiny Bow',
    description: 'A teeny bow for a teeny friend.',
    slot: 'squishy',
    rarity: 'common',
    sources: ['starter'],
    tradable: false,
    visual: {
      anchor: 'crown',
      pieces: [
        {
          shape: 'teardrop',
          at: [-0.25, 0.05, 0],
          size: [0.4, 0.5, 0.25],
          turn: [0, 0, 90],
          color: '#ff6f91',
        },
        {
          shape: 'teardrop',
          at: [0.25, 0.05, 0],
          size: [0.4, 0.5, 0.25],
          turn: [0, 0, -90],
          color: '#ff6f91',
        },
        { shape: 'ellipsoid', at: [0, 0.05, 0], size: [0.2, 0.2, 0.2], color: '#e24f78' },
      ],
    },
  },
  {
    id: 'snuggle-scarf',
    name: 'Snuggle Scarf',
    description: 'Keeps little necks toasty.',
    slot: 'squishy',
    rarity: 'common',
    sources: ['found', 'boutique'],
    boutiquePrice: 15, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      anchor: 'neck',
      pieces: [
        { shape: 'ellipsoid', at: [0, 0, 0], size: [1.12, 0.18, 1.12], color: '#ff8fab' },
        { shape: 'capsule', at: [0.2, -0.13, -0.55], size: [0.14, 0.26, 0.07], color: '#ff8fab' },
      ],
    },
  },
  {
    id: 'tiny-top-hat',
    name: 'Tiny Top Hat',
    description: 'Very fancy. Very tiny. Very proud.',
    slot: 'squishy',
    rarity: 'uncommon',
    sources: ['found', 'boutique'],
    boutiquePrice: 30, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      anchor: 'crown',
      pieces: [
        { shape: 'ellipsoid', at: [0, 0, 0], size: [0.9, 0.1, 0.9], color: '#3b2a3f' },
        { shape: 'capsule', at: [0, 0.3, 0], size: [0.55, 0.6, 0.55], color: '#3b2a3f' },
        { shape: 'capsule', at: [0, 0.125, 0], size: [0.575, 0.125, 0.575], color: '#ff6f91' },
      ],
    },
  },
  {
    id: 'tiny-crown',
    name: 'Tiny Crown',
    description: 'For the bravest, snuggliest squishy around.',
    slot: 'squishy',
    rarity: 'epic',
    sources: ['found', 'boutique'],
    boutiquePrice: 100, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      anchor: 'crown',
      pieces: [
        { shape: 'capsule', at: [0, 0.1, 0], size: [0.65, 0.2, 0.65], color: '#ffd84d' },
        { shape: 'cone', at: [0, 0.3, -0.25], size: [0.175, 0.3, 0.175], color: '#ffd84d' },
        { shape: 'cone', at: [-0.225, 0.275, 0.125], size: [0.15, 0.25, 0.15], color: '#ffd84d' },
        { shape: 'cone', at: [0.225, 0.275, 0.125], size: [0.15, 0.25, 0.15], color: '#ffd84d' },
      ],
    },
  },
  {
    id: 'tiny-witch-hat',
    name: 'Tiny Witch Hat',
    description: 'Makes any squishy look extra magical.',
    slot: 'squishy',
    rarity: 'rare',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 60, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      anchor: 'crown',
      pieces: [
        { shape: 'ellipsoid', at: [0, 0, 0], size: [1.05, 0.1, 1.05], color: '#6b4a7a' },
        {
          shape: 'cone',
          at: [0, 0.35, 0],
          size: [0.55, 0.75, 0.55],
          turn: [0, 0, 8],
          color: '#6b4a7a',
        },
      ],
    },
  },
  {
    id: 'pumpkin-cap',
    name: 'Pumpkin Cap',
    description: 'A snug little pumpkin with a curly stem.',
    slot: 'squishy',
    rarity: 'uncommon',
    season: 'halloween',
    sources: ['found', 'boutique'],
    boutiquePrice: 30, // TUNE: by rarity (#45)
    tradable: true,
    visual: {
      anchor: 'crown',
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.075, 0], size: [0.85, 0.45, 0.85], color: '#ff9a3c' },
        { shape: 'capsule', at: [0, 0.35, 0], size: [0.125, 0.25, 0.125], color: '#6a9a5a' },
      ],
    },
  },
];

// ── Squishy costumes (#515): Halloween trick-or-treat prizes ─────────────
// Still one per squishy: a costume takes the accessory's place (owner, #515).
// Each piece can sit on its own anchor: a hat on the `crown`, a collar on the
// `neck`, wings and capes on the `back` (the torso's, between the shoulders,
// +z away from the face). Only trick-or-treating gives them (source
// `trick-or-treat`), and they wear their rarity's finish like Keeper costumes.
// TUNE: shapes and colours are first guesses to judge in the gallery
// (`/gallery.html?accessory=…`).
const SQUISHY_COSTUMES: ClothingItem[] = [
  {
    id: 'candy-corn-scarf',
    name: 'Candy Corn Scarf',
    description: 'Stripy, snuggly and only a tiny bit sticky.',
    slot: 'squishy',
    rarity: 'common',
    season: 'halloween',
    sources: ['trick-or-treat'],
    tradable: true,
    visual: {
      anchor: 'neck',
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.08, 0], size: [1.18, 0.17, 1.18], color: '#ffd23f' },
        { shape: 'ellipsoid', at: [0, 0.02, 0], size: [1.13, 0.15, 1.13], color: '#ff8a3c' },
        { shape: 'ellipsoid', at: [0, 0.1, 0], size: [1.04, 0.11, 1.04], color: '#fff6e0' },
        {
          shape: 'ellipsoid',
          at: [0.22, -0.02, -0.56],
          size: [0.27, 0.25, 0.17],
          color: '#fff6e0',
        },
        {
          shape: 'capsule',
          at: [0.17, -0.22, -0.56],
          size: [0.16, 0.3, 0.08],
          turn: [0, 0, 12],
          color: '#ffd23f',
        },
        {
          shape: 'capsule',
          at: [0.3, -0.2, -0.54],
          size: [0.15, 0.26, 0.08],
          turn: [0, 0, -18],
          color: '#ff8a3c',
        },
      ],
    },
  },
  {
    id: 'little-bat-wings',
    name: 'Little Bat Wings',
    description: 'Tiny wings for big night-time zooms.',
    slot: 'squishy',
    rarity: 'uncommon',
    season: 'halloween',
    sources: ['trick-or-treat'],
    tradable: true,
    visual: {
      anchor: 'back',
      pieces: [
        {
          shape: 'teardrop',
          at: [-0.68, 0.31, 0.15],
          size: [0.2, 0.93, 0.08],
          turn: [0, 0, 64.592],
          color: '#4a3560',
        },
        {
          shape: 'ellipsoid',
          at: [-0.456, -0.026, 0.15],
          size: [0.567, 0.54, 0.07],
          color: '#4a3560',
        },
        {
          shape: 'ellipsoid',
          at: [-0.696, 0.122, 0.15],
          size: [0.483, 0.46, 0.07],
          color: '#4a3560',
        },
        {
          shape: 'ellipsoid',
          at: [-0.92, 0.28, 0.15],
          size: [0.357, 0.34, 0.07],
          color: '#4a3560',
        },
        {
          shape: 'teardrop',
          at: [0.68, 0.31, 0.15],
          size: [0.2, 0.93, 0.08],
          turn: [0, 0, -64.592],
          color: '#4a3560',
        },
        {
          shape: 'ellipsoid',
          at: [0.456, -0.026, 0.15],
          size: [0.567, 0.54, 0.07],
          color: '#4a3560',
        },
        {
          shape: 'ellipsoid',
          at: [0.696, 0.122, 0.15],
          size: [0.483, 0.46, 0.07],
          color: '#4a3560',
        },
        { shape: 'ellipsoid', at: [0.92, 0.28, 0.15], size: [0.357, 0.34, 0.07], color: '#4a3560' },
        {
          shape: 'ellipsoid',
          at: [-0.664, 0.132, 0.11],
          size: [0.52, 0.26, 0.05],
          turn: [0, 0, -25.408],
          color: '#a58bd0',
        },
        {
          shape: 'ellipsoid',
          at: [0.664, 0.132, 0.11],
          size: [0.52, 0.26, 0.05],
          turn: [0, 0, 25.408],
          color: '#a58bd0',
        },
      ],
    },
  },
  {
    id: 'pumpkin-patch-hood',
    name: 'Pumpkin Patch Hood',
    description: 'A plump pumpkin hood with a leafy collar.',
    slot: 'squishy',
    rarity: 'uncommon',
    season: 'halloween',
    sources: ['trick-or-treat'],
    tradable: true,
    visual: {
      anchor: 'crown',
      pieces: [
        { shape: 'ellipsoid', at: [-0.24, 0.02, 0.06], size: [0.55, 0.6, 0.9], color: '#ff8a3c' },
        { shape: 'ellipsoid', at: [0.24, 0.02, 0.06], size: [0.55, 0.6, 0.9], color: '#ff8a3c' },
        { shape: 'ellipsoid', at: [0, 0.05, 0.06], size: [0.6, 0.66, 0.96], color: '#ff9a4c' },
        {
          shape: 'capsule',
          at: [0, 0.4, 0.06],
          size: [0.13, 0.26, 0.13],
          turn: [0, 0, 12],
          color: '#6a8a4a',
        },
        {
          shape: 'teardrop',
          at: [0.17, 0.4, 0.06],
          size: [0.22, 0.32, 0.07],
          turn: [0, 0, -62],
          color: '#7cc96a',
        },
        {
          anchor: 'neck',
          shape: 'ellipsoid',
          at: [0, 0, 0],
          size: [1.1, 0.12, 1.1],
          color: '#6aae5a',
        },
        {
          anchor: 'neck',
          shape: 'teardrop',
          at: [-0.3, -0.06, -0.42],
          size: [0.24, 0.32, 0.07],
          turn: [-20, 0, 150],
          color: '#7cc96a',
        },
        {
          anchor: 'neck',
          shape: 'teardrop',
          at: [0.3, -0.06, -0.42],
          size: [0.24, 0.32, 0.07],
          turn: [-20, 0, -150],
          color: '#7cc96a',
        },
      ],
    },
  },
  {
    id: 'ghostie-sheet',
    name: 'Ghostie Sheet',
    description: "Boo! (It's a very friendly boo.)",
    slot: 'squishy',
    rarity: 'rare',
    season: 'halloween',
    sources: ['trick-or-treat'],
    tradable: true,
    visual: {
      anchor: 'crown',
      hood: true,
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.06, 0.16], size: [1.3, 0.92, 1.2], color: '#e9ecff' },
        {
          shape: 'teardrop',
          at: [0.05, 0.44, 0.26],
          size: [0.34, 0.42, 0.34],
          turn: [-30, 0, -20],
          color: '#e9ecff',
        },
        {
          shape: 'ellipsoid',
          at: [-0.17, 0.22, -0.38],
          size: [0.15, 0.24, 0.08],
          color: '#3b2a3f',
        },
        { shape: 'ellipsoid', at: [0.17, 0.22, -0.38], size: [0.15, 0.24, 0.08], color: '#3b2a3f' },
        {
          anchor: 'body',
          shape: 'teardrop',
          at: [0, 0.5, 0.4],
          size: [1.36, 0.96, 0.28],
          color: '#e9ecff',
        },
        {
          anchor: 'body',
          shape: 'ellipsoid',
          at: [-0.4, 0.1, 0.36],
          size: [0.36, 0.24, 0.26],
          color: '#b9b3e6',
        },
        {
          anchor: 'body',
          shape: 'ellipsoid',
          at: [-0.14, 0.1, 0.48],
          size: [0.36, 0.24, 0.26],
          color: '#b9b3e6',
        },
        {
          anchor: 'body',
          shape: 'ellipsoid',
          at: [0.14, 0.1, 0.48],
          size: [0.36, 0.24, 0.26],
          color: '#b9b3e6',
        },
        {
          anchor: 'body',
          shape: 'ellipsoid',
          at: [0.4, 0.1, 0.36],
          size: [0.36, 0.24, 0.26],
          color: '#b9b3e6',
        },
      ],
    },
  },
  {
    id: 'little-witch',
    name: 'Little Witch',
    description: 'A floppy hat, a twirly cape and big magic energy.',
    slot: 'squishy',
    rarity: 'rare',
    season: 'halloween',
    sources: ['trick-or-treat'],
    tradable: true,
    visual: {
      anchor: 'crown',
      pieces: [
        { shape: 'ellipsoid', at: [0, 0, 0], size: [1.1, 0.1, 1.1], color: '#5b3f78' },
        { shape: 'cone', at: [0, 0.33, 0.02], size: [0.56, 0.62, 0.56], color: '#5b3f78' },
        { shape: 'capsule', at: [0, 0.07, 0], size: [0.6, 0.11, 0.6], color: '#ff9a3c' },
        { shape: 'capsule', at: [0, 0.07, -0.3], size: [0.15, 0.13, 0.04], color: '#ffd84d' },
        {
          shape: 'cone',
          at: [0.13, 0.66, 0.04],
          size: [0.22, 0.3, 0.22],
          turn: [0, 0, -55],
          color: '#5b3f78',
        },
        {
          anchor: 'neck',
          shape: 'ellipsoid',
          at: [0, 0, -0.52],
          size: [0.16, 0.16, 0.08],
          color: '#ffd84d',
        },
        {
          anchor: 'back',
          shape: 'teardrop',
          at: [0, -0.34, 0.06],
          size: [1.2, 1.06, 0.1],
          color: '#5b3f78',
        },
      ],
    },
  },
  {
    id: 'vampire-cape',
    name: 'Vampire Cape',
    description: 'A swishy cape with a very tall collar. Bleh!',
    slot: 'squishy',
    rarity: 'epic',
    season: 'halloween',
    sources: ['trick-or-treat'],
    tradable: true,
    visual: {
      anchor: 'back',
      pieces: [
        { shape: 'teardrop', at: [0, -0.36, 0.08], size: [1.3, 1.12, 0.12], color: '#2e2140' },
        { shape: 'teardrop', at: [0, -0.38, 0.02], size: [1.12, 0.96, 0.06], color: '#c23a5a' },
        {
          anchor: 'neck',
          shape: 'teardrop',
          at: [-0.42, 0.22, 0.12],
          size: [0.5, 0.72, 0.08],
          turn: [-10, -20, 32],
          color: '#2e2140',
        },
        {
          anchor: 'neck',
          shape: 'teardrop',
          at: [0.42, 0.22, 0.12],
          size: [0.5, 0.72, 0.08],
          turn: [-10, 20, -32],
          color: '#2e2140',
        },
        {
          anchor: 'neck',
          shape: 'teardrop',
          at: [-0.4, 0.2, 0.09],
          size: [0.36, 0.54, 0.06],
          turn: [-10, -20, 32],
          color: '#c23a5a',
        },
        {
          anchor: 'neck',
          shape: 'teardrop',
          at: [0.4, 0.2, 0.09],
          size: [0.36, 0.54, 0.06],
          turn: [-10, 20, -32],
          color: '#c23a5a',
        },
        {
          anchor: 'neck',
          shape: 'ellipsoid',
          at: [0, 0, 0],
          size: [1.1, 0.16, 1.1],
          color: '#2e2140',
        },
        {
          anchor: 'neck',
          shape: 'ellipsoid',
          at: [0, 0, -0.55],
          size: [0.18, 0.18, 0.1],
          color: '#e0405f',
        },
      ],
    },
  },
];

// ── Milestone pieces (design doc §24, #44): signature, account-bound ──────
// Earned only from Keeper milestones (`data/milestones.ts`), never found,
// sold or traded, so wearing one shows what you did. The found Cozy Apron
// and Squishy Net stay found-only (DECISIONS "Patch Coins and the Boutique
// (#45)"); these are new pieces.
const MILESTONE: ClothingItem[] = [
  {
    id: 'explorers-hat',
    name: "Explorer's Hat",
    description: 'A wide brim for squinting at faraway tiles.',
    slot: 'hat',
    rarity: 'uncommon',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.1, 0.04], size: [1.6, 0.1, 1.6], color: '#c9a36b' },
        { shape: 'ellipsoid', at: [0, 0.14, 0.04], size: [0.92, 0.5, 0.92], color: '#c9a36b' },
        { shape: 'capsule', at: [0, -0.02, 0.04], size: [0.94, 0.12, 0.94], color: '#7a4f2c' },
      ],
    },
  },
  {
    id: 'cartographer-cape',
    name: 'Cartographer Cape',
    description: 'Every tile you claimed, stitched on in tiny squares.',
    slot: 'back',
    rarity: 'rare',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.35, 0.5], size: [1.2, 1.6, 0.2], color: '#7fc8a9' },
        { shape: 'ellipsoid', at: [-0.25, -0.2, 0.62], size: [0.3, 0.3, 0.06], color: '#ffd166' },
        { shape: 'ellipsoid', at: [0.25, -0.6, 0.62], size: [0.3, 0.3, 0.06], color: '#a8d8ff' },
        { shape: 'ellipsoid', at: [0, 0.42, 0], size: [1.1, 0.18, 1.1], color: '#fff1e6' },
      ],
    },
  },
  {
    id: 'crown-of-the-gap',
    name: 'Crown of the Gap',
    description: "Worn by the kindest Keeper in Juniper's Gap.",
    slot: 'hat',
    rarity: 'legendary',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'capsule', at: [0, 0.02, 0.04], size: [0.96, 0.22, 0.96], color: '#ffd166' },
        { shape: 'cone', at: [-0.3, 0.28, -0.2], size: [0.2, 0.32, 0.2], color: '#ffd166' },
        { shape: 'cone', at: [0, 0.32, -0.34], size: [0.22, 0.38, 0.22], color: '#ffd166' },
        { shape: 'cone', at: [0.3, 0.28, -0.2], size: [0.2, 0.32, 0.2], color: '#ffd166' },
        { shape: 'ellipsoid', at: [0, 0.06, -0.47], size: [0.16, 0.16, 0.08], color: '#7fc97f' },
      ],
    },
  },
  {
    id: 'collectors-satchel',
    name: "Collector's Satchel",
    description: 'A pocket for every new friend’s favorite snack.',
    slot: 'back',
    rarity: 'rare',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0.3, -0.3, 0.45], size: [0.7, 0.6, 0.4], color: '#b07f5a' },
        { shape: 'ellipsoid', at: [0.3, -0.12, 0.47], size: [0.72, 0.24, 0.42], color: '#8a5a3c' },
        {
          shape: 'capsule',
          at: [0, 0.1, 0],
          size: [0.08, 1.3, 1.1],
          turn: [0, 0, 35],
          color: '#8a5a3c',
        },
      ],
    },
  },
  {
    id: 'rainbow-jacket',
    name: 'Rainbow Jacket',
    description: 'One stripe for every kind of squishy you befriended.',
    slot: 'top',
    rarity: 'epic',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.02, 0], size: [1.12, 1.08, 1.12], color: '#ff8fab' },
        { shape: 'ellipsoid', at: [0, 0.2, 0], size: [1.14, 0.16, 1.14], color: '#ffd166' },
        { shape: 'ellipsoid', at: [0, 0.0, 0], size: [1.14, 0.16, 1.14], color: '#9fd88b' },
        { shape: 'ellipsoid', at: [0, -0.2, 0], size: [1.14, 0.16, 1.14], color: '#7fa8f0' },
        { shape: 'ellipsoid', at: [0, 0.46, 0], size: [0.74, 0.2, 0.76], color: '#c4a8f0' },
      ],
    },
  },
  {
    id: 'evolvers-goggles',
    name: "Evolver's Goggles",
    description: 'For watching very closely when someone evolves.',
    slot: 'hair-accessory',
    rarity: 'rare',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [-0.32, 0, -0.1], size: [0.5, 0.5, 0.3], color: '#a8e6ff' },
        { shape: 'ellipsoid', at: [0.32, 0, -0.1], size: [0.5, 0.5, 0.3], color: '#a8e6ff' },
        { shape: 'capsule', at: [0, 0, 0], size: [1.3, 0.14, 0.14], color: '#b07f5a' },
      ],
    },
  },
  {
    id: 'prism-boots',
    name: 'Prism Boots',
    description: 'Every step leaves a tiny rainbow behind.',
    slot: 'shoes',
    rarity: 'epic',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.1, 0], size: [1.15, 1.2, 1.1], color: '#d6c4ff' },
        { shape: 'capsule', at: [0, 0.75, 0.15], size: [0.8, 0.9, 0.7], color: '#a8e6ff' },
        { shape: 'ellipsoid', at: [0, 1.15, 0.15], size: [0.9, 0.2, 0.8], color: '#ffd6f0' },
      ],
    },
  },
  {
    id: 'heart-mittens',
    name: 'Heart Mittens',
    description: 'Extra soft, for extra gentle pats.',
    slot: 'held',
    rarity: 'rare',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0, 0], size: [0.55, 0.62, 0.45], color: '#ff8fab' },
        { shape: 'ellipsoid', at: [0.22, 0.1, -0.05], size: [0.2, 0.3, 0.2], color: '#ff8fab' },
        { shape: 'ellipsoid', at: [0, 0.02, -0.23], size: [0.24, 0.22, 0.05], color: '#fff1e6' },
        { shape: 'ellipsoid', at: [0, -0.32, 0], size: [0.5, 0.14, 0.42], color: '#fff1e6' },
      ],
    },
  },
  {
    id: 'hearthkeeper-lantern',
    name: 'Hearthkeeper Lantern',
    description: 'Carries a little Hearthfire spark wherever you go.',
    slot: 'held',
    rarity: 'rare',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'capsule', at: [0, 0.38, 0], size: [0.12, 0.5, 0.12], color: '#7a4f2c' },
        { shape: 'ellipsoid', at: [0, -0.05, 0], size: [0.56, 0.66, 0.56], color: '#ffb347' },
        { shape: 'teardrop', at: [0, -0.02, 0], size: [0.24, 0.34, 0.24], color: '#fff3a8' },
        { shape: 'cone', at: [0, 0.32, 0], size: [0.5, 0.26, 0.5], color: '#7a4f2c' },
      ],
    },
  },
  {
    id: 'ember-cloak',
    name: 'Ember Cloak',
    description: 'Warm as a fire on a long night of keeping watch.',
    slot: 'back',
    rarity: 'epic',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.35, 0.5], size: [1.25, 1.65, 0.2], color: '#e2603f' },
        { shape: 'ellipsoid', at: [0, -0.95, 0.52], size: [1.0, 0.4, 0.22], color: '#ffb347' },
        { shape: 'ellipsoid', at: [0, 0.42, 0], size: [1.1, 0.2, 1.1], color: '#ffd166' },
      ],
    },
  },
  {
    id: 'brave-scarf',
    name: 'Brave Scarf',
    description: 'For the Keeper who found the way through the Hollow.',
    slot: 'top',
    rarity: 'uncommon',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.44, 0], size: [1.12, 0.24, 1.12], color: '#ffb347' },
        { shape: 'ellipsoid', at: [0, 0.34, 0], size: [1.1, 0.16, 1.1], color: '#fff3a8' },
        { shape: 'capsule', at: [0.24, 0.1, -0.5], size: [0.2, 0.48, 0.1], color: '#ffb347' },
        { shape: 'capsule', at: [0.36, 0.06, -0.48], size: [0.18, 0.42, 0.1], color: '#fff3a8' },
      ],
    },
  },
  // Seeker (#199): exploring your land, search spot by search spot.
  {
    id: 'seekers-bandana',
    name: "Seeker's Bandana",
    description: 'Keeps the leaves out of your eyes while you peek under rocks.',
    slot: 'hair-accessory',
    rarity: 'uncommon',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'capsule', at: [0, 0.02, 0], size: [1.24, 0.2, 1.1], color: '#7fc8a9' },
        { shape: 'ellipsoid', at: [0.12, -0.04, 0.58], size: [0.26, 0.2, 0.12], color: '#7fc8a9' },
        { shape: 'capsule', at: [0.2, -0.22, 0.6], size: [0.12, 0.32, 0.08], color: '#5aa889' },
      ],
    },
  },
  {
    id: 'trail-pack',
    name: 'Trail Pack',
    description: 'A pocket for every shiny thing you dug up.',
    slot: 'back',
    rarity: 'rare',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.3, 0.5], size: [0.85, 0.95, 0.5], color: '#c9a36b' },
        { shape: 'ellipsoid', at: [0, -0.05, 0.56], size: [0.88, 0.3, 0.52], color: '#7a4f2c' },
        { shape: 'capsule', at: [0, -0.62, 0.78], size: [0.6, 0.12, 0.12], color: '#ffd166' },
        { shape: 'ellipsoid', at: [0, 0.42, 0], size: [1.1, 0.16, 1.1], color: '#7a4f2c' },
      ],
    },
  },
  {
    id: 'mossy-crown',
    name: 'Mossy Crown',
    description: 'Grown from every corner of land you know by heart.',
    slot: 'hat',
    rarity: 'epic',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'capsule', at: [0, 0.02, 0.04], size: [0.98, 0.24, 0.98], color: '#93d6a0' },
        { shape: 'ellipsoid', at: [-0.3, 0.2, -0.24], size: [0.26, 0.22, 0.26], color: '#ffd6f0' },
        { shape: 'ellipsoid', at: [0.3, 0.2, -0.24], size: [0.26, 0.22, 0.26], color: '#fff3a8' },
        { shape: 'ellipsoid', at: [0, 0.24, -0.4], size: [0.3, 0.26, 0.3], color: '#ff8fab' },
        { shape: 'cone', at: [0, 0.3, 0.3], size: [0.18, 0.3, 0.18], color: '#6a9a5a' },
      ],
    },
  },
  {
    id: 'lightbringer-wings',
    name: 'Lightbringer Wings',
    description: 'They glow softly, so nobody gets lost in the dark.',
    slot: 'back',
    rarity: 'legendary',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        {
          shape: 'ellipsoid',
          at: [-0.62, 0.32, 0.6],
          size: [1.0, 1.2, 0.08],
          turn: [0, -20, 25],
          color: '#fff3a8',
        },
        {
          shape: 'ellipsoid',
          at: [0.62, 0.32, 0.6],
          size: [1.0, 1.2, 0.08],
          turn: [0, 20, -25],
          color: '#fff3a8',
        },
        {
          shape: 'ellipsoid',
          at: [-0.5, -0.35, 0.6],
          size: [0.6, 0.66, 0.08],
          turn: [0, -20, -20],
          color: '#ffe0f0',
        },
        {
          shape: 'ellipsoid',
          at: [0.5, -0.35, 0.6],
          size: [0.6, 0.66, 0.08],
          turn: [0, 20, 20],
          color: '#ffe0f0',
        },
      ],
    },
  },
  {
    // The Halloween track's top tier (design doc §24 "Seasonal"): that
    // season's legendary costume, only earned in its window.
    id: 'harvest-moon-costume',
    name: 'Harvest Moon Costume',
    description: 'Be a round, glowing moon with a sleepy smile.',
    slot: 'costume',
    rarity: 'legendary',
    season: 'halloween',
    sources: ['milestone'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.2, 0], size: [0.98, 0.7, 1.0], color: '#ffe8a3' },
        { shape: 'ellipsoid', at: [0, 0.22, 0.12], size: [1.02, 0.62, 0.92], color: '#ffe8a3' },
        { shape: 'ellipsoid', at: [-0.3, 0.3, -0.36], size: [0.18, 0.18, 0.08], color: '#f2c96b' },
        { shape: 'ellipsoid', at: [0.26, -0.2, -0.46], size: [0.22, 0.22, 0.08], color: '#f2c96b' },
        { shape: 'arc', at: [0, -0.02, -0.5], size: [0.3, 0.1, 0.05], color: '#6b4a7a' },
      ],
    },
  },
];

// ── Halloween costumes (#261): head to toe, Common to Mythic ─────────────
// Each piece sits on a body socket (`on`: head, legs, arms, …), so a costume
// follows every base's own proportions; pieces without one use the costume
// box. They hide the other worn pieces and tuck the hair away (#42), and
// wear their rarity's finish (ART_BIBLE §1.4). Found in the Halloween window;
// Common to Legendary also sell on its rack, the Mythic Hollow Man never.
const HALLOWEEN_COSTUMES: ClothingItem[] = [
  {
    id: 'hollow-man-costume',
    name: 'Hollow Man',
    description: 'Tall, hushed and very mysterious. Shh!',
    slot: 'costume',
    rarity: 'mythic',
    season: 'halloween',
    sources: ['found'], // Mythic: found only, never sold (#261)
    tradable: true,
    // The Hollow Man himself (#21): the client draws his own shape, tall and
    // thin with long arms, a ragged hem and gold eyes, in the Mythic shimmer,
    // in place of the Keeper.
    visual: { pieces: [], replacesBody: true, model: 'hollow-man' },
  },
  {
    id: 'marigold-calavera',
    name: 'Marigold Calavera',
    description: 'Bright flowers and big smiles, to remember the ones we love.',
    slot: 'costume',
    rarity: 'legendary',
    season: 'halloween',
    sources: ['found', 'boutique', 'trick-or-treat'],
    boutiquePrice: 320, // TUNE: the costume rack (#261)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, 0.1, 0.1],
          size: [1.1, 1.06, 1.04],
          color: '#3b2a3f',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, -0.04, -0.06],
          size: [1.02, 0.96, 0.98],
          color: '#fff4e6',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [-0.19, 0, -0.5],
          size: [0.3, 0.3, 0.08],
          color: '#ff6fa8',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0.19, 0, -0.5],
          size: [0.3, 0.3, 0.08],
          color: '#2ec4b6',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [-0.19, 0, -0.535],
          size: [0.15, 0.17, 0.07],
          color: '#3b2a3f',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0.19, 0, -0.535],
          size: [0.15, 0.17, 0.07],
          color: '#3b2a3f',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [-0.16, 0.04, -0.57],
          size: [0.05, 0.05, 0.03],
          color: '#ffffff',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0.22, 0.04, -0.57],
          size: [0.05, 0.05, 0.03],
          color: '#ffffff',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, -0.14, -0.555],
          size: [0.08, 0.06, 0.05],
          color: '#3b2a3f',
        },
        {
          shape: 'arc',
          on: 'head',
          at: [0, -0.27, -0.52],
          size: [0.24, 0.08, 0.05],
          turn: [0, 180, 0],
          color: '#b8487a',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [-0.32, -0.17, -0.41],
          size: [0.08, 0.08, 0.05],
          color: '#9b5de5',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0.32, -0.17, -0.41],
          size: [0.08, 0.08, 0.05],
          color: '#9b5de5',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [-0.36, 0.4, -0.12],
          size: [0.26, 0.2, 0.26],
          color: '#ff9f1c',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [-0.13, 0.52, -0.2],
          size: [0.26, 0.2, 0.26],
          color: '#ffb627',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0.13, 0.52, -0.2],
          size: [0.26, 0.2, 0.26],
          color: '#ff9f1c',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0.36, 0.4, -0.12],
          size: [0.26, 0.2, 0.26],
          color: '#ffb627',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.04, 0],
          size: [1.12, 1.2, 1.16],
          color: '#fff8ec',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.42, 0.05],
          size: [1.25, 0.32, 1.2],
          color: '#2ec4b6',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.05, -0.59],
          size: [0.22, 0.22, 0.06],
          color: '#ff6fa8',
        },
        {
          shape: 'ellipsoid',
          on: 'bottom',
          at: [0, -0.25, 0],
          size: [1.25, 1.1, 1.25],
          color: '#9b5de5',
        },
        {
          shape: 'ellipsoid',
          on: 'bottom',
          at: [0, -0.75, 0],
          size: [1.55, 0.8, 1.5],
          color: '#ff6fa8',
        },
        {
          shape: 'ellipsoid',
          on: 'bottom',
          at: [0, -1.1, 0],
          size: [1.75, 0.5, 1.65],
          color: '#ffd23f',
        },
        { shape: 'capsule', on: 'arms', at: [0, 0, 0], size: [1.35, 1.02, 1.35], color: '#fff8ec' },
        {
          shape: 'ellipsoid',
          on: 'shoes',
          at: [0, 0.06, -0.02],
          size: [1.12, 1.12, 1.08],
          color: '#e86a4a',
        },
      ],
    },
  },
  {
    id: 'zippy-hedgehog',
    name: 'Zippy Hedgehog',
    description: 'Soft quills, a snuffly snout and a scarf that goes zoom.',
    slot: 'costume',
    rarity: 'epic',
    season: 'halloween',
    sources: ['found', 'boutique', 'trick-or-treat'],
    boutiquePrice: 260, // TUNE: the costume rack (#261)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, 0.08, 0.14],
          size: [1.2, 1.18, 1.1],
          color: '#4f8fe8',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [0, 0.74, 0.12],
          size: [0.24, 0.28, 0.24],
          turn: [12, 0, 0],
          color: '#3a6fd0',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [-0.34, 0.66, 0.16],
          size: [0.24, 0.28, 0.24],
          turn: [15, 0, -30],
          color: '#3a6fd0',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [0.34, 0.66, 0.16],
          size: [0.24, 0.28, 0.24],
          turn: [15, 0, 30],
          color: '#3a6fd0',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [-0.56, 0.38, 0.2],
          size: [0.24, 0.28, 0.24],
          turn: [20, 0, -60],
          color: '#3a6fd0',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [0.56, 0.38, 0.2],
          size: [0.24, 0.28, 0.24],
          turn: [20, 0, 60],
          color: '#3a6fd0',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [-0.24, 0.5, 0.56],
          size: [0.24, 0.28, 0.24],
          turn: [60, 0, -25],
          color: '#3a6fd0',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [0.24, 0.5, 0.56],
          size: [0.24, 0.28, 0.24],
          turn: [60, 0, 25],
          color: '#3a6fd0',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [0, 0.2, 0.72],
          size: [0.24, 0.28, 0.24],
          turn: [85, 0, 0],
          color: '#3a6fd0',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [-0.34, 0.48, -0.02],
          size: [0.24, 0.24, 0.12],
          color: '#4f8fe8',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0.34, 0.48, -0.02],
          size: [0.24, 0.24, 0.12],
          color: '#4f8fe8',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [-0.34, 0.48, -0.07],
          size: [0.13, 0.13, 0.06],
          color: '#ffb3c7',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0.34, 0.48, -0.07],
          size: [0.13, 0.13, 0.06],
          color: '#ffb3c7',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, -0.11, -0.5],
          size: [0.26, 0.17, 0.12],
          color: '#fff1d6',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, -0.07, -0.575],
          size: [0.1, 0.08, 0.07],
          color: '#3b2a3f',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.04, 0],
          size: [1.12, 1.2, 1.16],
          color: '#4f8fe8',
        },
        {
          shape: 'ellipsoid',
          on: 'bottom',
          at: [0, 0, 0],
          size: [1.1, 1.15, 1.1],
          color: '#4f8fe8',
        },
        { shape: 'capsule', on: 'legs', at: [0, 0, 0], size: [1.4, 1, 1.4], color: '#4f8fe8' },
        { shape: 'capsule', on: 'arms', at: [0, 0, 0], size: [1.35, 1.02, 1.35], color: '#4f8fe8' },
        {
          shape: 'ellipsoid',
          on: 'hands',
          at: [0, 0, 0],
          size: [1.15, 1.15, 1.15],
          color: '#4f8fe8',
        },
        {
          shape: 'ellipsoid',
          on: 'shoes',
          at: [0, 0.06, -0.02],
          size: [1.12, 1.12, 1.08],
          color: '#ffb84d',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, -0.08, -0.52],
          size: [0.66, 0.72, 0.2],
          color: '#fff1d6',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.62, 0],
          size: [1.14, 0.26, 1.18],
          color: '#ffd23f',
        },
        {
          shape: 'teardrop',
          on: 'top',
          at: [0.5, 0.45, 0.62],
          size: [0.28, 0.75, 0.1],
          turn: [0, 30, -70],
          color: '#2ec4b6',
        },
      ],
    },
  },
  {
    id: 'glow-moth',
    name: 'Glow Moth',
    description: 'Fuzzy, fluttery, and a little bit glowy at night.',
    slot: 'costume',
    rarity: 'epic',
    season: 'halloween',
    sources: ['found', 'boutique', 'trick-or-treat'],
    boutiquePrice: 260, // TUNE: the costume rack (#261)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, 0.08, 0.14],
          size: [1.2, 1.18, 1.1],
          color: '#cdb8f0',
        },
        {
          shape: 'capsule',
          on: 'head',
          at: [-0.2, 0.68, 0.05],
          size: [0.06, 0.42, 0.06],
          turn: [0, 0, -20],
          color: '#8a62c4',
        },
        {
          shape: 'capsule',
          on: 'head',
          at: [0.2, 0.68, 0.05],
          size: [0.06, 0.42, 0.06],
          turn: [0, 0, 20],
          color: '#8a62c4',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [-0.33, 0.95, 0.05],
          size: [0.26, 0.36, 0.07],
          turn: [0, 0, -30],
          color: '#e9dcf7',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [0.33, 0.95, 0.05],
          size: [0.26, 0.36, 0.07],
          turn: [0, 0, 30],
          color: '#e9dcf7',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.04, 0],
          size: [1.12, 1.2, 1.16],
          color: '#cdb8f0',
        },
        {
          shape: 'ellipsoid',
          on: 'bottom',
          at: [0, 0, 0],
          size: [1.1, 1.15, 1.1],
          color: '#cdb8f0',
        },
        { shape: 'capsule', on: 'legs', at: [0, 0, 0], size: [1.4, 1, 1.4], color: '#cdb8f0' },
        { shape: 'capsule', on: 'arms', at: [0, 0, 0], size: [1.35, 1.02, 1.35], color: '#cdb8f0' },
        {
          shape: 'ellipsoid',
          on: 'hands',
          at: [0, 0, 0],
          size: [1.15, 1.15, 1.15],
          color: '#fff4e0',
        },
        {
          shape: 'ellipsoid',
          on: 'shoes',
          at: [0, 0.06, -0.02],
          size: [1.12, 1.12, 1.08],
          color: '#fff4e0',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.48, 0],
          size: [1.3, 0.36, 1.25],
          color: '#fff4e0',
        },
        {
          shape: 'teardrop',
          on: 'back',
          at: [-0.85, 0.25, 0.6],
          size: [1, 1.45, 0.08],
          turn: [0, -15, 60],
          color: '#b39ddb',
        },
        {
          shape: 'teardrop',
          on: 'back',
          at: [0.85, 0.25, 0.6],
          size: [1, 1.45, 0.08],
          turn: [0, 15, -60],
          color: '#b39ddb',
        },
        {
          shape: 'teardrop',
          on: 'back',
          at: [-0.6, -0.45, 0.6],
          size: [0.6, 0.85, 0.08],
          turn: [0, -10, 120],
          color: '#a58fd6',
        },
        {
          shape: 'teardrop',
          on: 'back',
          at: [0.6, -0.45, 0.6],
          size: [0.6, 0.85, 0.08],
          turn: [0, 10, -120],
          color: '#a58fd6',
        },
        {
          shape: 'ellipsoid',
          on: 'back',
          at: [-1, 0.32, 0.55],
          size: [0.34, 0.34, 0.05],
          color: '#fff1a8',
          glow: true,
        },
        {
          shape: 'ellipsoid',
          on: 'back',
          at: [1, 0.32, 0.55],
          size: [0.34, 0.34, 0.05],
          color: '#fff1a8',
          glow: true,
        },
        {
          shape: 'ellipsoid',
          on: 'back',
          at: [-0.68, -0.55, 0.55],
          size: [0.22, 0.22, 0.05],
          color: '#a8f0ff',
          glow: true,
        },
        {
          shape: 'ellipsoid',
          on: 'back',
          at: [0.68, -0.55, 0.55],
          size: [0.22, 0.22, 0.05],
          color: '#a8f0ff',
          glow: true,
        },
      ],
    },
  },
  {
    id: 'moonbroom-witch',
    name: 'Moonbroom Witch',
    description: 'A starry cloak, stripy socks and a broom for crumbs.',
    slot: 'costume',
    rarity: 'rare',
    season: 'halloween',
    sources: ['found', 'boutique', 'trick-or-treat'],
    boutiquePrice: 210, // TUNE: the costume rack (#261)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, 0.05, 0.12],
          size: [1.12, 1.08, 1.04],
          color: '#4a3557',
        },
        {
          shape: 'ellipsoid',
          on: 'hat',
          at: [0, -0.1, 0.04],
          size: [1.5, 0.1, 1.5],
          color: '#6b4a7a',
        },
        { shape: 'cone', on: 'hat', at: [0, 0.38, 0.04], size: [0.8, 1, 0.8], color: '#6b4a7a' },
        {
          shape: 'capsule',
          on: 'hat',
          at: [0, 0.02, 0.04],
          size: [0.82, 0.14, 0.82],
          color: '#ffa552',
        },
        {
          shape: 'arc',
          on: 'hat',
          at: [0.02, 0.32, -0.17],
          size: [0.2, 0.2, 0.05],
          turn: [0, 180, 40],
          color: '#ffd84d',
        },
        {
          shape: 'teardrop',
          on: 'back',
          at: [0, -0.45, 0.52],
          size: [1.35, 1.9, 0.18],
          turn: [0, 0, 180],
          color: '#3b3f7a',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.45, 0],
          size: [1.15, 0.22, 1.12],
          color: '#3b3f7a',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.04, 0],
          size: [1.12, 1.2, 1.16],
          color: '#8a62c4',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0.22, 0.12, -0.59],
          size: [0.1, 0.1, 0.05],
          color: '#ffd84d',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [-0.18, -0.15, -0.58],
          size: [0.08, 0.08, 0.05],
          color: '#ffd84d',
        },
        {
          shape: 'ellipsoid',
          on: 'bottom',
          at: [0, -0.45, 0],
          size: [1.55, 1.3, 1.5],
          color: '#8a62c4',
        },
        { shape: 'capsule', on: 'legs', at: [0, 0, 0], size: [1.3, 1, 1.3], color: '#fff4dc' },
        {
          shape: 'ellipsoid',
          on: 'legs',
          at: [0, 0.1, 0],
          size: [1.42, 0.16, 1.42],
          color: '#6b4a7a',
        },
        {
          shape: 'ellipsoid',
          on: 'legs',
          at: [0, -0.25, 0],
          size: [1.42, 0.16, 1.42],
          color: '#6b4a7a',
        },
        {
          shape: 'ellipsoid',
          on: 'shoes',
          at: [0, 0.08, 0.05],
          size: [1.12, 1.15, 1],
          color: '#4a3557',
        },
        {
          shape: 'cone',
          on: 'shoes',
          at: [0, 0.3, -0.62],
          size: [0.5, 0.7, 0.5],
          turn: [-70, 0, 0],
          color: '#4a3557',
        },
        {
          shape: 'ellipsoid',
          on: 'shoes',
          at: [0, 0.32, -0.5],
          size: [0.2, 0.2, 0.12],
          color: '#ffd84d',
        },
        { shape: 'capsule', on: 'arms', at: [0, 0, 0], size: [1.35, 1.02, 1.35], color: '#8a62c4' },
        { shape: 'capsule', on: 'held', at: [0, 0.4, 0], size: [0.1, 1.3, 0.1], color: '#a0703f' },
        { shape: 'cone', on: 'held', at: [0, -0.35, 0], size: [0.45, 0.5, 0.45], color: '#e8c068' },
        {
          shape: 'capsule',
          on: 'held',
          at: [0, -0.12, 0],
          size: [0.24, 0.08, 0.24],
          color: '#6b4a7a',
        },
      ],
    },
  },
  {
    id: 'bat-buddy',
    name: 'Bat Buddy',
    description: 'Velvet ears, flappy wings, and zero upside-down naps.',
    slot: 'costume',
    rarity: 'rare',
    season: 'halloween',
    sources: ['found', 'boutique', 'trick-or-treat'],
    boutiquePrice: 210, // TUNE: the costume rack (#261)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, 0.08, 0.14],
          size: [1.2, 1.18, 1.1],
          color: '#4a3557',
        },
        {
          shape: 'cone',
          on: 'head',
          at: [-0.34, 0.62, 0.12],
          size: [0.32, 0.5, 0.22],
          turn: [0, 0, -18],
          color: '#4a3557',
        },
        {
          shape: 'cone',
          on: 'head',
          at: [0.34, 0.62, 0.12],
          size: [0.32, 0.5, 0.22],
          turn: [0, 0, 18],
          color: '#4a3557',
        },
        {
          shape: 'cone',
          on: 'head',
          at: [-0.34, 0.6, 0],
          size: [0.16, 0.3, 0.06],
          turn: [0, 0, -18],
          color: '#ff9db5',
        },
        {
          shape: 'cone',
          on: 'head',
          at: [0.34, 0.6, 0],
          size: [0.16, 0.3, 0.06],
          turn: [0, 0, 18],
          color: '#ff9db5',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.04, 0],
          size: [1.12, 1.2, 1.16],
          color: '#4a3557',
        },
        {
          shape: 'ellipsoid',
          on: 'bottom',
          at: [0, 0, 0],
          size: [1.1, 1.15, 1.1],
          color: '#4a3557',
        },
        { shape: 'capsule', on: 'legs', at: [0, 0, 0], size: [1.4, 1, 1.4], color: '#4a3557' },
        { shape: 'capsule', on: 'arms', at: [0, 0, 0], size: [1.35, 1.02, 1.35], color: '#4a3557' },
        {
          shape: 'ellipsoid',
          on: 'hands',
          at: [0, 0, 0],
          size: [1.15, 1.15, 1.15],
          color: '#4a3557',
        },
        {
          shape: 'ellipsoid',
          on: 'shoes',
          at: [0, 0.06, -0.02],
          size: [1.12, 1.12, 1.08],
          color: '#4a3557',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, -0.08, -0.52],
          size: [0.62, 0.7, 0.2],
          color: '#b39ddb',
        },
        {
          shape: 'teardrop',
          on: 'back',
          at: [-0.7, 0.15, 0.6],
          size: [0.85, 1.35, 0.08],
          turn: [0, -15, 60],
          color: '#5c4470',
        },
        {
          shape: 'teardrop',
          on: 'back',
          at: [0.7, 0.15, 0.6],
          size: [0.85, 1.35, 0.08],
          turn: [0, 15, -60],
          color: '#5c4470',
        },
        {
          shape: 'teardrop',
          on: 'back',
          at: [-0.55, -0.4, 0.6],
          size: [0.45, 0.65, 0.08],
          turn: [0, -10, 110],
          color: '#5c4470',
        },
        {
          shape: 'teardrop',
          on: 'back',
          at: [0.55, -0.4, 0.6],
          size: [0.45, 0.65, 0.08],
          turn: [0, 10, -110],
          color: '#5c4470',
        },
      ],
    },
  },
  {
    id: 'star-striker',
    name: 'Star Striker',
    description: 'Lace up, run fast, and do a happy goal dance.',
    slot: 'costume',
    rarity: 'uncommon',
    season: 'halloween',
    sources: ['found', 'boutique', 'trick-or-treat'],
    boutiquePrice: 160, // TUNE: the costume rack (#261)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, 0.12, 0.12],
          size: [1.1, 1.02, 1.04],
          color: '#2ec4b6',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, 0.24, 0.02],
          size: [1.14, 0.16, 1.1],
          color: '#fffafc',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, 0.25, -0.56],
          size: [0.13, 0.13, 0.05],
          color: '#ffd23f',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.04, 0],
          size: [1.12, 1.2, 1.16],
          color: '#2ec4b6',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.6, -0.1],
          size: [0.72, 0.14, 0.8],
          color: '#fffafc',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.02, -0.52],
          size: [0.98, 0.16, 0.18],
          color: '#fffafc',
        },
        {
          shape: 'capsule',
          on: 'arms',
          at: [0, 0.25, 0],
          size: [1.35, 0.55, 1.35],
          color: '#2ec4b6',
        },
        {
          shape: 'ellipsoid',
          on: 'bottom',
          at: [0, 0, 0],
          size: [1.1, 1.2, 1.1],
          color: '#fffafc',
        },
        {
          shape: 'capsule',
          on: 'legs',
          at: [0, -0.15, 0],
          size: [1.35, 0.75, 1.35],
          color: '#2ec4b6',
        },
        {
          shape: 'ellipsoid',
          on: 'legs',
          at: [0, -0.1, -0.45],
          size: [0.8, 0.55, 0.25],
          color: '#fffafc',
        },
        {
          shape: 'ellipsoid',
          on: 'shoes',
          at: [0, 0.05, 0],
          size: [1.1, 1.1, 1.05],
          color: '#3b2a3f',
        },
        {
          shape: 'ellipsoid',
          on: 'shoes',
          at: [0, 0.1, -0.2],
          size: [1.14, 0.35, 0.6],
          color: '#7cc96a',
        },
        {
          shape: 'ellipsoid',
          on: 'held',
          at: [0.25, -0.1, -0.1],
          size: [1.1, 1.1, 1.1],
          color: '#fffafc',
        },
        {
          shape: 'ellipsoid',
          on: 'held',
          at: [0.25, -0.1, -0.66],
          size: [0.17, 0.17, 0.05],
          color: '#3b2a3f',
        },
        {
          shape: 'ellipsoid',
          on: 'held',
          at: [-0.02, 0.17, -0.52],
          size: [0.17, 0.17, 0.05],
          color: '#3b2a3f',
        },
        {
          shape: 'ellipsoid',
          on: 'held',
          at: [0.52, 0.17, -0.52],
          size: [0.17, 0.17, 0.05],
          color: '#3b2a3f',
        },
        {
          shape: 'ellipsoid',
          on: 'held',
          at: [-0.02, -0.37, -0.52],
          size: [0.17, 0.17, 0.05],
          color: '#3b2a3f',
        },
        {
          shape: 'ellipsoid',
          on: 'held',
          at: [0.52, -0.37, -0.52],
          size: [0.17, 0.17, 0.05],
          color: '#3b2a3f',
        },
      ],
    },
  },
  {
    id: 'cozy-mummy',
    name: 'Cozy Mummy',
    description: 'Wrapped up snug. One eye open, just in case.',
    slot: 'costume',
    rarity: 'uncommon',
    season: 'halloween',
    sources: ['found', 'boutique', 'trick-or-treat'],
    boutiquePrice: 160, // TUNE: the costume rack (#261)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, 0.02, 0],
          size: [1.08, 1.06, 1.06],
          color: '#f3ead7',
        },
        {
          shape: 'capsule',
          on: 'head',
          at: [0, 0.25, -0.46],
          size: [1, 0.13, 0.14],
          turn: [0, 0, -8],
          color: '#e3d4b4',
        },
        {
          shape: 'capsule',
          on: 'head',
          at: [-0.17, 0, -0.52],
          size: [0.48, 0.14, 0.1],
          turn: [0, 0, -25],
          color: '#e3d4b4',
        },
        {
          shape: 'capsule',
          on: 'head',
          at: [0, -0.42, -0.42],
          size: [0.9, 0.13, 0.14],
          turn: [0, 0, 6],
          color: '#e3d4b4',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0.19, 0, -0.54],
          size: [0.15, 0.18, 0.06],
          color: '#3b2a3f',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0.22, 0.04, -0.575],
          size: [0.05, 0.05, 0.03],
          color: '#ffffff',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0.3, -0.16, -0.47],
          size: [0.14, 0.08, 0.04],
          color: '#ff9db5',
        },
        {
          shape: 'arc',
          on: 'head',
          at: [0, -0.27, -0.53],
          size: [0.12, 0.05, 0.04],
          turn: [0, 180, 0],
          color: '#3b2a3f',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [-0.18, 0.6, 0.02],
          size: [0.26, 0.32, 0.14],
          turn: [0, 0, 70],
          color: '#f8f0dd',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [0.18, 0.6, 0.02],
          size: [0.26, 0.32, 0.14],
          turn: [0, 0, -70],
          color: '#f8f0dd',
        },
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, 0.6, 0.02],
          size: [0.13, 0.13, 0.13],
          color: '#e3d4b4',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.04, 0],
          size: [1.12, 1.2, 1.16],
          color: '#f3ead7',
        },
        {
          shape: 'ellipsoid',
          on: 'bottom',
          at: [0, 0, 0],
          size: [1.1, 1.15, 1.1],
          color: '#f3ead7',
        },
        { shape: 'capsule', on: 'legs', at: [0, 0, 0], size: [1.4, 1, 1.4], color: '#f3ead7' },
        { shape: 'capsule', on: 'arms', at: [0, 0, 0], size: [1.35, 1.02, 1.35], color: '#f3ead7' },
        {
          shape: 'ellipsoid',
          on: 'hands',
          at: [0, 0, 0],
          size: [1.15, 1.15, 1.15],
          color: '#f3ead7',
        },
        {
          shape: 'ellipsoid',
          on: 'shoes',
          at: [0, 0.06, -0.02],
          size: [1.12, 1.12, 1.08],
          color: '#f3ead7',
        },
        {
          shape: 'capsule',
          on: 'top',
          at: [0, 0.2, 0],
          size: [1.2, 0.14, 1.22],
          turn: [0, 0, 6],
          color: '#e3d4b4',
        },
        {
          shape: 'capsule',
          on: 'top',
          at: [0, -0.25, 0],
          size: [1.2, 0.14, 1.22],
          turn: [0, 0, -5],
          color: '#e3d4b4',
        },
        {
          shape: 'teardrop',
          on: 'top',
          at: [0.4, -0.5, -0.52],
          size: [0.18, 0.5, 0.06],
          turn: [0, 0, 170],
          color: '#f3ead7',
        },
      ],
    },
  },
  {
    id: 'patch-scarecrow',
    name: 'Patch Scarecrow',
    description: 'Straw, patches and a crow pal who never leaves.',
    slot: 'costume',
    rarity: 'common',
    season: 'halloween',
    sources: ['found', 'boutique', 'trick-or-treat'],
    boutiquePrice: 120, // TUNE: the costume rack (#261)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'ellipsoid',
          on: 'hat',
          at: [0, -0.12, 0.04],
          size: [1.75, 0.1, 1.75],
          color: '#e8c068',
        },
        {
          shape: 'capsule',
          on: 'hat',
          at: [0, 0.1, 0.04],
          size: [0.88, 0.45, 0.88],
          color: '#e8c068',
        },
        {
          shape: 'capsule',
          on: 'hat',
          at: [0, -0.02, 0.04],
          size: [0.9, 0.12, 0.9],
          color: '#b8487a',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [-0.5, 0.1, 0.15],
          size: [0.24, 0.38, 0.22],
          turn: [0, 0, -150],
          color: '#f2d27a',
        },
        {
          shape: 'teardrop',
          on: 'head',
          at: [0.5, 0.1, 0.15],
          size: [0.24, 0.38, 0.22],
          turn: [0, 0, 150],
          color: '#f2d27a',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.04, 0],
          size: [1.12, 1.2, 1.16],
          color: '#e86a4a',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, -0.12, -0.47],
          size: [0.72, 0.62, 0.28],
          color: '#5b7fbf',
        },
        {
          shape: 'capsule',
          on: 'top',
          at: [-0.25, 0.28, -0.55],
          size: [0.12, 0.45, 0.1],
          color: '#5b7fbf',
        },
        {
          shape: 'capsule',
          on: 'top',
          at: [0.25, 0.28, -0.55],
          size: [0.12, 0.45, 0.1],
          color: '#5b7fbf',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [-0.17, -0.2, -0.62],
          size: [0.2, 0.2, 0.05],
          color: '#ffc94d',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0.22, 0.1, -0.62],
          size: [0.16, 0.14, 0.08],
          color: '#3b2a3f',
        },
        {
          shape: 'cone',
          on: 'top',
          at: [0.32, 0.1, -0.63],
          size: [0.06, 0.1, 0.06],
          turn: [0, 0, 90],
          color: '#ffa552',
        },
        {
          shape: 'ellipsoid',
          on: 'bottom',
          at: [0, 0, 0],
          size: [1.1, 1.15, 1.1],
          color: '#5b7fbf',
        },
        { shape: 'capsule', on: 'legs', at: [0, 0, 0], size: [1.4, 1, 1.4], color: '#5b7fbf' },
        {
          shape: 'ellipsoid',
          on: 'legs',
          at: [0, 0.05, -0.55],
          size: [0.6, 0.4, 0.15],
          color: '#7cc96a',
        },
        {
          shape: 'cone',
          on: 'legs',
          at: [0, -0.5, 0],
          size: [1.6, 0.4, 1.6],
          turn: [180, 0, 0],
          color: '#f2d27a',
        },
        { shape: 'capsule', on: 'arms', at: [0, 0, 0], size: [1.35, 1.02, 1.35], color: '#e86a4a' },
        {
          shape: 'teardrop',
          on: 'hands',
          at: [0, 0.45, 0],
          size: [1.4, 0.9, 1.4],
          turn: [180, 0, 0],
          color: '#f2d27a',
        },
        {
          shape: 'ellipsoid',
          on: 'shoes',
          at: [0, 0.06, -0.02],
          size: [1.12, 1.12, 1.08],
          color: '#8a5a3c',
        },
      ],
    },
  },
  {
    id: 'candy-corn-cutie',
    name: 'Candy Corn Cutie',
    description: 'Yellow, orange and a sweet little point on top.',
    slot: 'costume',
    rarity: 'common',
    season: 'halloween',
    sources: ['found', 'boutique', 'trick-or-treat'],
    boutiquePrice: 120, // TUNE: the costume rack (#261)
    tradable: true,
    visual: {
      pieces: [
        {
          shape: 'ellipsoid',
          on: 'head',
          at: [0, 0.08, 0.14],
          size: [1.2, 1.18, 1.1],
          color: '#ff8a4a',
        },
        {
          shape: 'cone',
          on: 'head',
          at: [0, 0.62, 0.14],
          size: [0.85, 0.85, 0.8],
          color: '#fff8ec',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.04, 0],
          size: [1.12, 1.2, 1.16],
          color: '#ff8a4a',
        },
        {
          shape: 'ellipsoid',
          on: 'bottom',
          at: [0, 0, 0],
          size: [1.1, 1.15, 1.1],
          color: '#ffd23f',
        },
        { shape: 'capsule', on: 'legs', at: [0, 0, 0], size: [1.4, 1, 1.4], color: '#ffd23f' },
        { shape: 'capsule', on: 'arms', at: [0, 0, 0], size: [1.35, 1.02, 1.35], color: '#ff8a4a' },
        {
          shape: 'ellipsoid',
          on: 'hands',
          at: [0, 0, 0],
          size: [1.15, 1.15, 1.15],
          color: '#ff8a4a',
        },
        {
          shape: 'ellipsoid',
          on: 'shoes',
          at: [0, 0.06, -0.02],
          size: [1.12, 1.12, 1.08],
          color: '#f2b72f',
        },
        {
          shape: 'ellipsoid',
          on: 'top',
          at: [0, 0.6, 0],
          size: [1.1, 0.18, 1.16],
          color: '#fff8ec',
        },
      ],
    },
  },
];

// ── Rekindled (#306 PR 5): found only in Hollowed villages ────────────────
// Lantern-glow trims and ember patches: the joy a village gets back, worn.
// Year-round, found only by the server's Rekindled tables
// (`data/server/hollowed-finds.ts`, CLAUDE.md rule 6), and kept, never traded, so the set (and its Rekindler
// title, `FOUND_SETS`) shows what the Keeper found. The glowing bits are lit
// from inside (`glow`), like a lantern on a grey street.
const REKINDLED: ClothingItem[] = [
  {
    id: 'lantern-glow-cap',
    name: 'Lantern-Glow Cap',
    description: 'A knitted cap with a warm, glowing trim. Grey streets look friendlier already.',
    slot: 'hat',
    rarity: 'uncommon',
    sources: ['found'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.04, 0.04], size: [1.02, 0.7, 1.04], color: '#8a5a7a' },
        {
          shape: 'ellipsoid',
          at: [0, -0.28, 0.04],
          size: [1.06, 0.16, 1.08],
          color: '#ffd27a',
          glow: true,
        },
        {
          shape: 'teardrop',
          at: [0, 0.38, 0.04],
          size: [0.22, 0.3, 0.22],
          color: '#ffb347',
          glow: true,
        },
      ],
    },
  },
  {
    id: 'ember-patch-jacket',
    name: 'Ember Patch Jacket',
    description: 'Patched with little embers that never go out. Toasty!',
    slot: 'top',
    rarity: 'rare',
    sources: ['found'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.02, 0], size: [1.12, 1.08, 1.12], color: '#6f8fae' },
        { shape: 'ellipsoid', at: [0, 0.46, 0], size: [0.74, 0.2, 0.76], color: '#ffd27a' },
        {
          shape: 'teardrop',
          at: [-0.24, 0.12, -0.54],
          size: [0.24, 0.22, 0.06],
          color: '#ff8a5c',
          glow: true,
        },
        {
          shape: 'ellipsoid',
          at: [0.26, -0.18, -0.52],
          size: [0.2, 0.2, 0.06],
          color: '#ffb347',
          glow: true,
        },
      ],
    },
  },
  {
    id: 'ember-patch-trousers',
    name: 'Ember Patch Trousers',
    description: 'Comfy trousers with a glowing ember patch on each knee.',
    slot: 'bottom',
    rarity: 'uncommon',
    sources: ['found'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.1, 0], size: [1.1, 1.2, 1.08], color: '#7a6a8f' },
        { shape: 'ellipsoid', at: [0, 0.34, 0], size: [1.08, 0.18, 1.08], color: '#c9a36b' },
        {
          shape: 'ellipsoid',
          at: [-0.26, -0.36, -0.5],
          size: [0.22, 0.22, 0.06],
          color: '#ff8a5c',
          glow: true,
        },
        {
          shape: 'ellipsoid',
          at: [0.26, -0.36, -0.5],
          size: [0.22, 0.22, 0.06],
          color: '#ff8a5c',
          glow: true,
        },
      ],
    },
  },
  {
    id: 'lantern-glow-boots',
    name: 'Lantern-Glow Boots',
    description: 'Every step leaves a tiny warm glow on the path home.',
    slot: 'shoes',
    rarity: 'rare',
    sources: ['found'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.1, 0], size: [1.15, 1.2, 1.1], color: '#8a5a3c' },
        { shape: 'capsule', at: [0, 0.75, 0.15], size: [0.8, 0.9, 0.7], color: '#8a5a3c' },
        {
          shape: 'ellipsoid',
          at: [0, 1.15, 0.15],
          size: [0.9, 0.2, 0.8],
          color: '#ffd27a',
          glow: true,
        },
      ],
    },
  },
  {
    id: 'joy-lantern-charm',
    name: 'Joy Lantern Charm',
    description: 'A tiny lantern with a heart-shaped flame. It brightens any grey place.',
    slot: 'held',
    rarity: 'epic',
    sources: ['found'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'capsule', at: [0, 0.38, 0], size: [0.12, 0.5, 0.12], color: '#7a4f2c' },
        { shape: 'ellipsoid', at: [0, -0.05, 0], size: [0.56, 0.66, 0.56], color: '#ffe0a8' },
        {
          shape: 'teardrop',
          at: [0, -0.02, 0],
          size: [0.26, 0.34, 0.26],
          color: '#ff6f91',
          glow: true,
        },
        { shape: 'cone', at: [0, 0.32, 0], size: [0.5, 0.26, 0.5], color: '#7a4f2c' },
      ],
    },
  },
];

// ── Cellar keepsakes (#306): one in each kind of Hollow's cellar ─────────
// The big chest's best loot (owner decision of 2026-10-22): a one-of-a-kind
// keepsake, a different one for each kind of Hollow, found only in its
// cellar. The rule that gives it lives on the server (the big chest in
// `data/server/cellar.ts`, CLAUDE.md rule 6), as every found piece's odds
// do; the names say which kind's it is. Kept, never traded, so
// wearing one shows the Keeper brought that kind of Hollow back. Each glows
// a little, like the candlelight they were found in.
const CELLAR_KEEPSAKES: ClothingItem[] = [
  {
    // The Lantern Tower's: a gold crown with the tower's own lantern on top.
    id: 'lamplighters-crown',
    name: "Lamplighter's Crown",
    description: 'A golden crown with a tiny lantern on top. It never, ever goes out.',
    slot: 'hat',
    rarity: 'legendary',
    sources: ['found'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'capsule', at: [0, 0.0, 0.04], size: [1.0, 0.26, 1.0], color: '#f2b84b' },
        { shape: 'cone', at: [-0.4, 0.22, -0.22], size: [0.16, 0.24, 0.16], color: '#f2b84b' },
        { shape: 'cone', at: [-0.22, 0.24, -0.4], size: [0.16, 0.26, 0.16], color: '#f2b84b' },
        { shape: 'cone', at: [0.22, 0.24, -0.4], size: [0.16, 0.26, 0.16], color: '#f2b84b' },
        { shape: 'cone', at: [0.4, 0.22, -0.22], size: [0.16, 0.24, 0.16], color: '#f2b84b' },
        { shape: 'ellipsoid', at: [0, 0.02, -0.5], size: [0.18, 0.18, 0.08], color: '#ff6f91' },
        { shape: 'capsule', at: [0, 0.2, 0.02], size: [0.42, 0.1, 0.42], color: '#7a4f2c' },
        {
          shape: 'ellipsoid',
          at: [0, 0.46, 0.02],
          size: [0.38, 0.46, 0.38],
          color: '#ffb347',
          glow: true,
        },
        { shape: 'capsule', at: [-0.12, 0.46, -0.16], size: [0.05, 0.42, 0.05], color: '#7a4f2c' },
        { shape: 'capsule', at: [0.12, 0.46, -0.16], size: [0.05, 0.42, 0.05], color: '#7a4f2c' },
        { shape: 'cone', at: [0, 0.76, 0.02], size: [0.46, 0.24, 0.46], color: '#7a4f2c' },
        { shape: 'ellipsoid', at: [0, 0.9, 0.02], size: [0.12, 0.12, 0.12], color: '#f2b84b' },
      ],
    },
  },
  {
    // The Workshop's: a big brass wrench with a glowing gear in its jaw.
    id: 'gearheart-wrench',
    name: 'Gearheart Wrench',
    description: 'A big brass wrench with a glowing gear. It fixes almost anything (not homework).',
    slot: 'held',
    rarity: 'legendary',
    sources: ['found'],
    tradable: false,
    visual: {
      // Held up and out from the hand, so the jaw shows beside the Keeper:
      // a brass shaft and an open jaw with a glowing gear.
      pieces: [
        {
          shape: 'capsule',
          at: [0.06, 0.08, 0.0],
          size: [0.3, 0.55, 0.3],
          turn: [0, 0, -35],
          color: '#8a5a3c',
        },
        {
          shape: 'capsule',
          at: [0.46, 0.66, 0.0],
          size: [0.24, 1.2, 0.24],
          turn: [0, 0, -35],
          color: '#e0b25a',
        },
        {
          shape: 'ellipsoid',
          at: [0.89, 1.27, 0.0],
          size: [1.0, 0.95, 0.32],
          turn: [0, 0, -35],
          color: '#e0b25a',
        },
        // The jaw's open notch: a dark bite out of the head's end.
        {
          shape: 'ellipsoid',
          at: [1.13, 1.61, 0.0],
          size: [0.46, 0.5, 0.36],
          turn: [0, 0, -35],
          color: '#4a3020',
        },
        {
          shape: 'ellipsoid',
          at: [0.84, 1.2, -0.14],
          size: [0.42, 0.42, 0.12],
          color: '#7fe4f5',
          glow: true,
        },
      ],
    },
  },
  {
    // The Old Well's: a sea-blue cape scattered with glowing wishing coins.
    id: 'wishing-well-cape',
    name: 'Wishing Well Cape',
    description: 'Sparkly wishing coins swirl all over it. Make a wish and give it a twirl!',
    slot: 'back',
    rarity: 'legendary',
    sources: ['found'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.4, 0.5], size: [1.3, 1.75, 0.22], color: '#4f7fc4' },
        { shape: 'ellipsoid', at: [0, 0.42, 0], size: [1.14, 0.22, 1.14], color: '#8fd3ff' },
        {
          shape: 'ellipsoid',
          at: [-0.3, -0.2, 0.63],
          size: [0.26, 0.26, 0.06],
          color: '#ffe48a',
          glow: true,
        },
        {
          shape: 'ellipsoid',
          at: [0.26, -0.56, 0.63],
          size: [0.28, 0.28, 0.06],
          color: '#ffe48a',
          glow: true,
        },
        {
          shape: 'ellipsoid',
          at: [-0.16, -0.94, 0.63],
          size: [0.24, 0.24, 0.06],
          color: '#ffe48a',
          glow: true,
        },
        { shape: 'ellipsoid', at: [0, 0.3, -0.52], size: [0.24, 0.24, 0.12], color: '#ffe48a' },
      ],
    },
  },
  {
    // The Bakery's: a cream baker's jacket with a big glowing pie badge.
    id: 'golden-baker-jacket',
    name: 'Golden Baker Jacket',
    description:
      'Smells like warm pie forever. The golden pie badge is only for the very best bakers.',
    slot: 'top',
    rarity: 'legendary',
    sources: ['found'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.02, 0], size: [1.14, 1.1, 1.14], color: '#fff3dc' },
        { shape: 'ellipsoid', at: [0, 0.46, 0], size: [0.8, 0.24, 0.82], color: '#f2b84b' },
        { shape: 'ellipsoid', at: [0, -0.4, 0], size: [1.16, 0.2, 1.16], color: '#f2b84b' },
        {
          shape: 'ellipsoid',
          at: [0, 0.04, -0.55],
          size: [0.46, 0.4, 0.1],
          color: '#ffb347',
          glow: true,
        },
        { shape: 'ellipsoid', at: [0, 0.12, -0.6], size: [0.36, 0.14, 0.06], color: '#c97a3c' },
      ],
    },
  },
  {
    // The Watchtower's: tall lookout boots with a glowing star on each cuff.
    id: 'lookout-boots',
    name: 'Lookout Boots',
    description: 'Tall boots for standing guard. The stars on top twinkle when a friend is near.',
    slot: 'shoes',
    rarity: 'legendary',
    sources: ['found'],
    tradable: false,
    visual: {
      // Bright watch-blue with gold cuffs and a big glowing star on each, so
      // they show at a glance.
      pieces: [
        { shape: 'ellipsoid', at: [0, 0.1, -0.08], size: [1.32, 1.3, 1.4], color: '#3f6fd6' },
        { shape: 'capsule', at: [0, 0.9, 0.12], size: [1.0, 1.2, 0.9], color: '#3f6fd6' },
        { shape: 'ellipsoid', at: [0, 1.45, 0.12], size: [1.22, 0.36, 1.1], color: '#f2b84b' },
        {
          shape: 'ellipsoid',
          at: [0, 0.95, -0.36],
          size: [0.64, 0.64, 0.14],
          color: '#fff3a8',
          glow: true,
        },
        { shape: 'ellipsoid', at: [0, -0.3, -0.5], size: [0.9, 0.24, 0.5], color: '#f2b84b' },
      ],
    },
  },
];

/**
 * The far Hollow's keepsakes (#306 6b, owner decision 4): one a house,
 * found in its attic's big chest. Found-only, untradable Legendaries, as the
 * cellars' are.
 */
const FAR_HOLLOW_KEEPSAKES: ClothingItem[] = [
  {
    // The Crooked House's: a deep plum cape with a glowing lantern-gold hem.
    id: 'lantern-keepers-cloak',
    name: "Lantern Keeper's Cloak",
    description:
      'A cosy plum cape with a hem that glows like a lantern. No room stays dark for long.',
    slot: 'back',
    rarity: 'legendary',
    sources: ['found'],
    tradable: false,
    visual: {
      pieces: [
        { shape: 'ellipsoid', at: [0, -0.4, 0.5], size: [1.3, 1.75, 0.22], color: '#5b3f8a' },
        { shape: 'ellipsoid', at: [0, 0.42, 0], size: [1.14, 0.22, 1.14], color: '#7a5bb0' },
        {
          shape: 'ellipsoid',
          at: [0, -1.2, 0.52],
          size: [1.34, 0.18, 0.26],
          color: '#ffc75a',
          glow: true,
        },
        {
          shape: 'ellipsoid',
          at: [0, 0.3, -0.52],
          size: [0.24, 0.24, 0.12],
          color: '#ffc75a',
          glow: true,
        },
      ],
    },
  },
];

/** Every clothing item, checked by `checkClothingData` in tests. */
export const CLOTHING: ClothingItem[] = [
  ...STARTER,
  ...EVERYDAY,
  ...HALLOWEEN,
  ...HALLOWEEN_COSTUMES,
  ...MILESTONE,
  ...REKINDLED,
  ...CELLAR_KEEPSAKES,
  ...FAR_HOLLOW_KEEPSAKES,
  ...SQUISHY,
  ...SQUISHY_COSTUMES,
];

/** The catalog by id. */
export const CLOTHING_BY_ID: ReadonlyMap<string, ClothingItem> = new Map(
  CLOTHING.map((item) => [item.id, item]),
);

/** Every account's items from the start (design doc §23: the wardrobe is never empty). */
export const STARTER_CLOTHING: readonly string[] = CLOTHING.filter((item) =>
  item.sources.includes('starter'),
).map((item) => item.id);

/** The squishy costumes (#515), for the trick-or-treat prize table to name. */
export const SQUISHY_COSTUME_IDS: readonly string[] = CLOTHING.filter(isSquishyCostume).map(
  (item) => item.id,
);
